import { useEffect, useState, type ComponentType, type ReactNode, type SVGProps } from "react";
import clsx from "clsx";
import {
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  EnvelopeIcon,
  HashtagIcon,
  IdentificationIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { formatDateTime } from "@/lib/format";
import {
  ansprechpartnerDisplayName,
  listGeschaeftspartnerAnsprechpartner,
  type Geschaeftspartner,
  type GeschaeftspartnerTyp,
  type KontaktAnsprechpartner,
} from "@/lib/kontakte";
import {
  adresseLines,
  copyToClipboard,
  formatSkonto,
  formatZahlungsart,
  formatZahlungsziel,
  hatEigeneZahlungsbedingungen,
  mapsUrlForAdresse,
  type DetailTab,
} from "./geschaeftspartnerDetailUtils";

const ANSPRECHPARTNER_PREVIEW = 3;

interface GeschaeftspartnerUebersichtProps {
  gp: Geschaeftspartner;
  typ: GeschaeftspartnerTyp;
  onEdit: () => void;
  onNavigate: (tab: DetailTab) => void;
}

export function GeschaeftspartnerUebersicht({
  gp,
  typ,
  onEdit,
  onNavigate,
}: GeschaeftspartnerUebersichtProps) {
  const isKunde = typ === "kunde";
  const email = gp.email?.trim() || "";
  const telefon = gp.telefon?.trim() || "";
  const lines = adresseLines(gp);
  const mapsUrl = mapsUrlForAdresse(gp);
  const effektiv = gp.zahlungsbedingungen_effektiv;
  const rechnung = gp.rechnungs_einstellungen;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-2">
        <Card title="Kontakt & Stammdaten" description="Zentrale Daten, wie sie auf Belegen und in Projekten verwendet werden.">
          <dl className="-my-1 divide-y divide-separator/60">
            <DetailRow
              icon={EnvelopeIcon}
              label="E-Mail"
              value={email || null}
              actions={
                email ? (
                  <>
                    <RowAction onClick={() => void copyToClipboard(email, "E-Mail")} title="Kopieren">
                      <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                    <RowAction href={`mailto:${email}`} title="E-Mail schreiben">
                      <EnvelopeIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                  </>
                ) : null
              }
            />
            <DetailRow
              icon={PhoneIcon}
              label="Telefon"
              value={telefon || null}
              actions={
                telefon ? (
                  <>
                    <RowAction onClick={() => void copyToClipboard(telefon, "Telefonnummer")} title="Kopieren">
                      <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                    <RowAction href={`tel:${telefon}`} title="Anrufen">
                      <PhoneIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                  </>
                ) : null
              }
            />
            <DetailRow
              icon={MapPinIcon}
              label="Adresse"
              value={
                lines.length > 0 ? (
                  <span className="block">
                    {lines.map((line, index) => (
                      <span key={`${index}-${line}`} className="block">
                        {line}
                      </span>
                    ))}
                  </span>
                ) : null
              }
              actions={
                lines.length > 0 ? (
                  <>
                    <RowAction
                      onClick={() => void copyToClipboard(lines.join("\n"), "Adresse")}
                      title="Kopieren"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                    {mapsUrl ? (
                      <RowAction href={mapsUrl} external title="In Karte öffnen">
                        <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
                      </RowAction>
                    ) : null}
                  </>
                ) : null
              }
            />
            {isKunde ? (
              <>
                <DetailRow
                  icon={IdentificationIcon}
                  label="Kundennummer"
                  hint="Hero-Kundennummer – erscheint auf Rechnungen"
                  value={gp.kundennummer?.trim() || null}
                  mono
                  actions={
                    gp.kundennummer?.trim() ? (
                      <RowAction
                        onClick={() => void copyToClipboard(gp.kundennummer!.trim(), "Kundennummer")}
                        title="Kopieren"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                      </RowAction>
                    ) : null
                  }
                />
                <DetailRow
                  icon={HashtagIcon}
                  label="Kundencode"
                  hint="Interne Projektnummern-Gruppe"
                  value={gp.kundencode != null ? String(gp.kundencode) : null}
                  mono
                />
              </>
            ) : (
              <DetailRow
                icon={IdentificationIcon}
                label="Lieferantennummer"
                value={gp.lieferantennummer?.trim() || null}
                mono
                actions={
                  gp.lieferantennummer?.trim() ? (
                    <RowAction
                      onClick={() =>
                        void copyToClipboard(gp.lieferantennummer!.trim(), "Lieferantennummer")
                      }
                      title="Kopieren"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                  ) : null
                }
              />
            )}
          </dl>
        </Card>

        <Card
          title="Notizen"
          description="Interne Hinweise zu diesem Kontakt – nicht für Kunden sichtbar."
          actions={
            <Button variant="ghost" size="sm" onClick={onEdit}>
              {gp.notizen?.trim() ? "Bearbeiten" : "Notiz hinzufügen"}
            </Button>
          }
        >
          {gp.notizen?.trim() ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-label">{gp.notizen}</p>
          ) : (
            <p className="text-sm text-label-tertiary">Noch keine Notizen hinterlegt.</p>
          )}
        </Card>
      </div>

      <div className="space-y-4">
        {isKunde ? (
          <Card
            title="Konditionen"
            actions={
              <Button variant="ghost" size="sm" onClick={() => onNavigate("zahlung")}>
                Anpassen
              </Button>
            }
          >
            <dl className="space-y-3">
              <KeyValue label="Zahlungsart" value={formatZahlungsart(effektiv?.zahlungsart)} />
              <KeyValue label="Zahlungsziel" value={formatZahlungsziel(effektiv)} />
              <KeyValue label="Skonto" value={formatSkonto(effektiv)} />
            </dl>
            <p className="mt-3 text-xs text-label-tertiary">
              {hatEigeneZahlungsbedingungen(gp)
                ? "Individuelle Zahlungsziele für diesen Kunden."
                : "Es gilt der Firmen-Standard."}
            </p>
            <div className="mt-4 border-t border-separator/60 pt-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-xs font-medium uppercase tracking-wide text-label-tertiary">
                  Rechnung
                </h3>
                <button
                  type="button"
                  onClick={() => onNavigate("rechnung")}
                  className="text-xs font-medium text-accent hover:underline"
                >
                  Einstellungen
                </button>
              </div>
              <dl className="mt-2 space-y-3">
                <KeyValue
                  label="Format"
                  value={rechnung?.rechnungsformat === "xrechnung" ? "X-Rechnung" : "ZUGFeRD"}
                />
                {rechnung?.leitweg_id ? (
                  <KeyValue label="Leitweg-ID" value={rechnung.leitweg_id} mono />
                ) : null}
                {rechnung?.ust_id ? <KeyValue label="USt-IdNr." value={rechnung.ust_id} mono /> : null}
                {rechnung?.reverse_charge_13b ? (
                  <div className="rounded-lg bg-status-orange/10 px-3 py-2 text-xs font-medium text-status-orange">
                    § 13b UStG – Reverse Charge aktiv
                  </div>
                ) : null}
              </dl>
            </div>
          </Card>
        ) : (
          <Card
            title="Einkauf"
            actions={
              <Button variant="ghost" size="sm" onClick={() => onNavigate("einkauf")}>
                Öffnen
              </Button>
            }
          >
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold tabular-nums text-label">
                {(gp.artikel_count ?? 0).toLocaleString("de-DE")}
              </span>
              <span className="text-sm text-label-secondary">Stamm-Artikel</span>
            </div>
            <p className="mt-2 text-sm text-label-secondary">
              {gp.artikel_count != null && gp.artikel_count > 0
                ? "Artikel dieses Lieferanten sind im Artikelstamm hinterlegt. Preise aus Eingangsrechnungen findest du im Bereich Einkauf."
                : "Noch keine Artikel dieses Lieferanten im Artikelstamm."}
            </p>
          </Card>
        )}

        <AnsprechpartnerPreview
          geschaeftspartnerId={gp.id}
          total={gp.ansprechpartner_count ?? 0}
          onShowAll={() => onNavigate("kontakte")}
        />

        <Card title="System">
          <dl className="space-y-3">
            <KeyValue label="Hero-ID" value={gp.hero_contact_id ? String(gp.hero_contact_id) : "—"} mono />
            <KeyValue label="Hero-Sync" value={formatDateTime(gp.hero_synced_at)} />
            <KeyValue label="Angelegt" value={formatDateTime(gp.created_at)} />
            <KeyValue label="Aktualisiert" value={formatDateTime(gp.updated_at)} />
          </dl>
        </Card>
      </div>
    </div>
  );
}

function AnsprechpartnerPreview({
  geschaeftspartnerId,
  total,
  onShowAll,
}: {
  geschaeftspartnerId: number;
  total: number;
  onShowAll: () => void;
}) {
  const [rows, setRows] = useState<KontaktAnsprechpartner[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    listGeschaeftspartnerAnsprechpartner(geschaeftspartnerId)
      .then((list) => {
        if (!cancelled) setRows(list);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [geschaeftspartnerId]);

  const count = rows?.length ?? total;
  const preview = rows?.slice(0, ANSPRECHPARTNER_PREVIEW) ?? [];

  return (
    <Card
      title="Ansprechpartner"
      actions={
        count > 0 ? (
          <Button variant="ghost" size="sm" onClick={onShowAll}>
            Alle anzeigen{count > ANSPRECHPARTNER_PREVIEW ? ` (${count})` : ""}
          </Button>
        ) : null
      }
      bodyClassName={rows && rows.length > 0 ? "px-2 py-2" : undefined}
    >
      {rows == null ? (
        <div className="space-y-2 px-3 py-2" aria-busy>
          {Array.from({ length: Math.min(Math.max(total, 1), ANSPRECHPARTNER_PREVIEW) }).map((_, i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-fill-quaternary" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          compact
          icon={<UsersIcon />}
          title="Keine Ansprechpartner"
          description="Lege Personen an, um sie in Projekten und Belegen auswählen zu können."
          action={
            <Button variant="secondary" size="sm" onClick={onShowAll}>
              Ansprechpartner anlegen
            </Button>
          }
        />
      ) : (
        <ul className="divide-y divide-separator/60">
          {preview.map((ap) => {
            const name = ansprechpartnerDisplayName(ap);
            const mail = ap.mail?.trim();
            const tel = ap.mobiltelefon?.trim() || ap.telefonnummer?.trim();
            return (
              <li key={ap.id} className="flex items-center gap-3 px-3 py-2.5">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fill-tertiary text-xs font-semibold text-label"
                  aria-hidden
                >
                  {initialsFromPerson(ap)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-label">{name}</div>
                  <div className="truncate text-xs text-label-secondary">
                    {ap.funktion?.trim() || mail || tel || "—"}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {mail ? (
                    <RowAction href={`mailto:${mail}`} title={`E-Mail an ${name}`}>
                      <EnvelopeIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                  ) : null}
                  {tel ? (
                    <RowAction href={`tel:${tel}`} title={`${name} anrufen`}>
                      <PhoneIcon className="h-4 w-4" aria-hidden />
                    </RowAction>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function initialsFromPerson(ap: KontaktAnsprechpartner): string {
  const v = (ap.vorname || "").trim();
  const n = (ap.nachname || "").trim();
  const initials = `${v[0] ?? ""}${n[0] ?? ""}`.toUpperCase();
  return initials || "?";
}

function DetailRow({
  icon: Icon,
  label,
  hint,
  value,
  mono,
  actions,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  hint?: string;
  value: ReactNode | null;
  mono?: boolean;
  actions?: ReactNode;
}) {
  const empty = value == null || value === "";
  return (
    <div className="group flex items-start gap-3 py-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-fill-quaternary text-label-secondary">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <dt className="text-xs font-medium uppercase tracking-wide text-label-tertiary">{label}</dt>
        <dd
          className={clsx(
            "mt-0.5 break-words text-sm",
            empty ? "text-label-tertiary" : "text-label",
            mono && !empty && "font-mono tabular-nums",
          )}
        >
          {empty ? "Nicht hinterlegt" : value}
        </dd>
        {hint ? <p className="mt-0.5 text-xs text-label-tertiary">{hint}</p> : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-0.5 opacity-70 transition group-hover:opacity-100">
          {actions}
        </div>
      ) : null}
    </div>
  );
}

function RowAction({
  children,
  title,
  href,
  external,
  onClick,
}: {
  children: ReactNode;
  title: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
}) {
  const className =
    "inline-flex h-8 w-8 items-center justify-center rounded-lg text-label-secondary transition hover:bg-fill-tertiary hover:text-label focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40";
  if (href) {
    return (
      <a
        href={href}
        title={title}
        aria-label={title}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={className}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" title={title} aria-label={title} onClick={onClick} className={className}>
      {children}
    </button>
  );
}

function KeyValue({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-sm text-label-secondary">{label}</dt>
      <dd className={clsx("text-right text-sm font-medium text-label", mono && "font-mono tabular-nums")}>
        {value}
      </dd>
    </div>
  );
}
